
import type { UseQueryOptions, UseQueryResult } from '@tanstack/react-query'

import { useQuery } from '@tanstack/react-query'
import _ from '../../utils/lodash-optimized'
import { useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'

import http from '../../lib/http'

export interface Patient extends Record<string, unknown> {
  accuity: string
  birthDate: string
  diagnosis: string
  firstName: string
  id: string
  lastEncounter: string
  lastName: string
  lastType: string | null
  openEncounterStatus: string | null
  organization: string | null
  other: any[]
  phone: string[]
  ssn: string
  memberId: string
  status: string
  urgent: string | null
  _clientName: string
  isVIPPatient?: boolean | null
  enrolled?: string
  mobile_applications_access: string[]
}

export enum StatusesEnum {
  'all' = 'all',
  'planned' = 'planned',
  'onhold' = 'onhold',
  'waitlist' = 'waitlist',
  'active' = 'active',
  'finished' = 'finished',
  'cancelled' = 'cancelled',
  'entered-in-error' = 'entered-in-error'
}

export interface QueryParams {
  anyName?: string
  sortBy: string
  sortDir: string
  managingOrg?: string
  scope?: string
  status: StatusesEnum
  count: number
  clientName?: string
  patientId?: string
  birthDate?: string
  memberId?: string
  phone?: string
  ssn?: string
  page: number
  opensearch?: boolean
  open?: boolean
  enrolled?: string
}

type QueryKeys = [string, QueryParams | null]

type Options = UseQueryOptions<Response, unknown, Response, QueryKeys>

interface Response {
  count: number
  page: number
  page_total: number
  total: number
  data: Patient[]
}

interface SearchValues {
  anyName: string
  scope: string
  status: StatusesEnum
  clientName: string
  managingOrg: string
  open?: boolean
  isVIPPatient?: boolean | null
  enrolled: string
}

export const defaultSearchValues: SearchValues = {
  anyName: '',
  scope: 'all',
  status: StatusesEnum.all,
  clientName: 'all',
  managingOrg: 'all',
  open: false,
  enrolled: 'all'
}

export function useAdvancedSearchHook (state: QueryParams, options: Omit<Options, 'queryKey'>): [UseQueryResult<Response | undefined, unknown>, () => void, boolean] {
  let [ , setURLSearchParams ] = useSearchParams()

  const enabled = useMemo(() => {
    return !_.isEqual({
      anyName: state.anyName ?? '',
      scope: state.scope ?? 'all',
      status: state.status || StatusesEnum.all,
      clientName: state.clientName ?? 'all',
      managingOrg: state.managingOrg ?? 'all',
      open: state.open ?? false,
      enrolled: state.enrolled ?? 'all'
    }, defaultSearchValues)
  }, [state.anyName, state.clientName, state.managingOrg, state.scope, state.status, state.open, state.enrolled])

  const query = useQuery({
    queryKey: [ 'patient-search', state ],
    // @ts-expect-error // it's okay
    queryFn: async ({ queryKey: [ _, params ] }) => {
      if (!params) {
        return
      }

      return await http.getRequest<Response>('/patients_search', params, { clientName: false })
    },
    ...options,
    enabled
  })

  const reset = useCallback((): void => {
    setURLSearchParams((sp) => {
      const newSP = new URLSearchParams(sp)
      sp.forEach((_, key) => {
        newSP.delete(key)
      })
      return newSP
    })
  }, [ setURLSearchParams ])

  return [ query, reset, enabled ]
}
